import { ROUTES } from './routes';

export const PERMISSIONS = {
  sitesView: 'sites:view',
  billingView: 'billing:view',
  transactionsView: 'transactions:view',
  disbursementView: 'disbursement:view',
  administratorsView: 'administrators:view',
} as const;

// The API sends `screens`, the allow-list of tabs the account holds. An API from before
// the allow-list sends only `hidden_screens`, and every tab not listed there is open.
// Site access itself is still enforced server-side by grants.
export const SCREENS = [
  { key: 'sites', permission: PERMISSIONS.sitesView, route: ROUTES.sites, label: 'Sites' },
  { key: 'billing', permission: PERMISSIONS.billingView, route: ROUTES.billing, label: 'Billing' },
  {
    key: 'transactions',
    permission: PERMISSIONS.transactionsView,
    route: ROUTES.transactions,
    label: 'Transactions',
  },
  {
    key: 'disbursement',
    permission: PERMISSIONS.disbursementView,
    route: ROUTES.disbursement,
    label: 'Disbursement',
  },
  {
    key: 'administrators',
    permission: PERMISSIONS.administratorsView,
    route: ROUTES.administrators,
    label: 'Administrators',
  },
] as const;

type Screen = (typeof SCREENS)[number];

export const screenLabel = (key: string): string =>
  SCREENS.find((screen) => screen.key === key)?.label ?? key;

export interface IScreenAccess {
  screens?: readonly string[];
  hidden_screens?: readonly string[];
}

export const visibleScreens = (access?: IScreenAccess | null): Screen[] => {
  const held = access?.screens;
  if (held) return SCREENS.filter((screen) => held.includes(screen.key));

  // Administrators did not exist before the allow-list, so the old hidden list never covers it.
  const hidden = access?.hidden_screens ?? [];
  return SCREENS.filter(
    (screen) => screen.key !== 'administrators' && !hidden.includes(screen.key),
  );
};

export const canView = (access: IScreenAccess | null | undefined, permission: string) =>
  visibleScreens(access).some((screen) => screen.permission === permission);

// Where to send an admin with no particular destination: the first screen they can see.
export const landingRoute = (access?: IScreenAccess | null) =>
  visibleScreens(access)[0]?.route ?? ROUTES.login;
